/**
 * What a reconciliation's difference is made of, in one sentence.
 *
 * WHY THIS EXISTS
 *
 * The reconcile screen could say a month was out by $102,754.40 and nothing on
 * it said why. The two lists underneath held the answer, but reading a list of
 * forty-seven transactions to learn "they are all uncategorized" is work the
 * screen can do. A red number with no instruction is a red number the operator
 * learns to look past.
 *
 * TWO CLAIMS, AND THE SECOND IS THE USEFUL ONE
 *
 *   1. WHAT IT IS MADE OF. How many items are waiting, and whether they account
 *      for the difference exactly. "Exactly" matters: if the items add up to
 *      the difference, categorizing them closes it and nothing else is wrong.
 *      If they do not, something else is going on and the operator should know
 *      that BEFORE working through the list.
 *   2. WHEN ONE ITEM IS THE WHOLE DIFFERENCE, it is named. This is the case
 *      that is genuinely one click from solved, and finding it by eye means
 *      scanning a column of amounts for one that matches a number at the top of
 *      the panel.
 *
 * IT DECIDES NOTHING. §6.5 keeps the classification with the operator, and
 * nothing here posts, categorizes or proposes an account. It narrows the
 * search; the operator still does the work. A screen that guessed which
 * transaction "must be" the difference and acted on it would be the thing that
 * rule exists to prevent.
 *
 * SIGNS. Everything arriving here is LEDGER sign, and `show` is the caller's
 * conversion into the account's normal sign -- the same one the rest of the
 * screen uses. Nothing in this file flips a sign itself.
 */

import type { ReconciliationPreview } from '../api.js';

/**
 * An item that is, on its own, the entire difference.
 *
 * WHY `bankAmountCents` AND NOT THE DIFFERENCE'S OWN SIGN. `differenceCents` is
 * statement minus books: positive when the bank holds more than the books do.
 * An unbooked transaction is in the bank and not in the books, so ADDING it to
 * the books moves them by its bank amount -- and it explains the difference
 * exactly when that amount equals the difference.
 *
 * Returns null when several items match, which is not a failure: two identical
 * $80.00 transfers are a real shape, and naming one of them would be a guess
 * dressed as a finding.
 */
function soleCause(preview: ReconciliationPreview): ReconciliationPreview['unbooked'][number] | null {
  if (preview.differenceCents === null || preview.differenceCents === 0) return null;
  const matches = preview.unbooked.filter(
    (row) => row.bankAmountCents === preview.differenceCents,
  );
  return matches.length === 1 ? matches[0]! : null;
}

export function DifferenceExplained({
  preview,
  show,
}: {
  preview: ReconciliationPreview;
  /** Ledger cents to the account's normal sign, formatted. The caller's `show`. */
  show: (ledgerCents: number) => string;
}) {
  const { differenceCents } = preview;
  if (differenceCents === null || differenceCents === 0) return null;

  const waiting = preview.unbooked.length;
  if (waiting === 0) {
    /*
     * A DIFFERENCE WITH NOTHING BEHIND IT. Every bank transaction is posted and
     * the balances still disagree, which is a different problem from a backlog:
     * a missed transaction, a wrong amount, or a statement balance typed wrong.
     * Saying so is more use than saying nothing, and it stops the operator
     * looking for a list that is not there.
     */
    return (
      <p className="notice notice-warn difference-explained">
        Nothing is waiting to be categorized on this account, so this difference is not a backlog.
        Check the statement balance above, and whether a transaction is missing from the sync.
      </p>
    );
  }

  const uncategorized = preview.unbooked.filter((row) => row.uncoded).length;
  const explained = preview.unbookedTotalCents === differenceCents;
  const only = soleCause(preview);

  return (
    <p className="notice difference-explained">
      {only !== null ? (
        <>
          <strong>{show(differenceCents)} is exactly one transaction</strong> —{' '}
          {only.counterparty ?? 'no counterparty'} on {only.date}. Categorize and post it and this
          month ties.
        </>
      ) : explained ? (
        <>
          <strong>
            {waiting} {waiting === 1 ? 'transaction' : 'transactions'} account for all of it
          </strong>
          {uncategorized > 0 && <> — {uncategorized} not categorized yet</>}. Clear the list below
          and this month ties.
        </>
      ) : (
        <>
          <strong>
            {waiting} {waiting === 1 ? 'transaction' : 'transactions'} waiting,{' '}
            {show(preview.unbookedTotalCents)} of {show(differenceCents)}
          </strong>
          . They do not account for all of it, so something else is out as well — a missed
          transaction, or a statement balance that does not match the document.
        </>
      )}
    </p>
  );
}
